import { BlogPost } from '../types';

const post: BlogPost = {
	slug: 'geschenke-fuer-grosse-geschwister',
	title: 'Geschenke für den frisch gebackenen großen Bruder oder die große Schwester (die wirklich helfen)',
	description:
		'Was schenkt man dem älteren Kind, wenn ein Baby dazukommt? Warum ein Geschenk als „großes Geschwisterkind" wichtiger ist, als es klingt – und wie man das Timing richtig hinbekommt.',
	date: '2026-03-11',
	heroImage: '/assets/hero/kid-2/classic_scene.png',
	tags: ['Geschenkideen', 'neues Geschwisterkind'],
	lang: 'de',
	dateLocale: 'de-DE',
	body: `Wenn ein neues Baby ankommt, bekommt das ältere Kind einen merkwürdigen Deal. Das Haus füllt sich mit Besuchern, und fast die gesamte Aufmerksamkeit geht an jemanden, der noch nicht einmal den Kopf selbst halten kann.

Ein Geschenk für das ältere Kind in diesem Moment geht eigentlich nicht um das Objekt. Es geht darum, ihm eine Rolle zu verleihen.

## Warum ein Geschenk als „großes Geschwisterkind" Planung wert ist

Kinder zwischen zwei und sechs Jahren verstehen im Allgemeinen, was gerade passiert, haben aber keine Strategie, damit umzugehen. Sie werden im eigentlichen Sinne nicht verdrängt, aber angesichts dessen, was sie vor Augen haben, ist diese Sorge nicht unvernünftig.

Dem älteren Kind etwas zu schenken, das es benennt – *großer Bruder*, *große Schwester* – verschiebt die Perspektive auf das Ereignis. Es ist nicht die Person, der etwas weggenommen wird, sondern die Person, die befördert wurde.

Das funktioniert besser, wenn das Geschenk spezifisch statt generisch ist. Ein T-Shirt mit der Aufschrift „Großer Bruder" ist in Ordnung. Ein Buch, in dem das ältere Kind der illustrierte Held ist und das Baby eine Nebenfigur in *seiner* Geschichte, bewirkt deutlich mehr.

## Was man schenken sollte

**Ein personalisiertes Buch, das es als großes Geschwisterkind zeigt.** Die stärkste Option. Es gibt dem Kind eine Geschichte, in der es bereits eine Rolle hat, und man kann es immer wieder lesen – wichtig, denn dieses Gefühl löst sich nicht an einem einzigen Nachmittag auf.

**Ein personalisiertes Malbuch.** Nützlich für das spezifische Problem der ersten Wochen mit Neugeborenem: lange Phasen, in denen ein Elternteil beschäftigt ist und das ältere Kind etwas Fesselndes braucht, das keine Aufsicht erfordert.

**Ein Erinnerungsstück mit beiden Kindern.** Eher ein Geschenk für die Eltern, aber es hält über Jahrzehnte.

**Etwas, das ihm ganz allein gehört.** Eine Tasse, eine Stofftasche, ein T-Shirt mit der eigenen Figur. Der Punkt: Das Baby hat keinerlei Anspruch darauf. Dreijährige nehmen das sehr ernst.

## Was man vermeiden sollte

- **Alles, was das Baby irgendwann erben wird.** Das untergräbt die ganze Botschaft.
- **Geschenke, die als „vom Baby" verpackt werden.** Ältere Kinder durchschauen das sofort, und es kommt eher als leicht beleidigend an.
- **Spielzeug, das ein Elternteil zum Bedienen braucht.** Die Zeit und Aufmerksamkeit der Eltern ist in den ersten Wochen die knappste Ressource im Haus.

## Das richtige Timing

Übergebt es an dem Tag, an dem das Baby nach Hause kommt, oder beim ersten Krankenhausbesuch. Das ist der Moment, in dem dem älteren Kind die Veränderung am stärksten bewusst wird.

Das bedeutet, im Voraus zu bestellen. Personalisierte Artikel werden auf Bestellung angefertigt – Illustrationserstellung, eure Freigabe, Druck, Versand –, also zwei bis drei Wochen einplanen. Da Entbindungstermine nur Schätzungen sind, ist eine Bestellung einen Monat im Voraus sinnvoll.

Seid ihr Verwandte auf Besuch und nicht die Eltern selbst: Bringt etwas für das ältere Kind mit. Alle anderen werden etwas für das Baby mitbringen, und das ältere Kind zählt mit.

## Ein Hinweis zum zweiten Kind

Macht ihr das für das erste Kind, plant es auch für das zweite ein paar Jahre später ein. Kinder führen darüber erstaunlich genau Buch, und „es gibt ein Buch über dich, aber nicht über mich" ist eine Kränkung mit langer Haltbarkeit.`,
	faqs: [
		{
			question: 'Was sollte man dem älteren Kind schenken, wenn ein neues Baby ankommt?',
			answer:
				'Etwas, das eindeutig ihm gehört und seine neue Rolle benennt. Ein personalisiertes Buch, das das ältere Kind als großen Bruder oder große Schwester zeigt, funktioniert besonders gut, weil es ihm eine erzählerische Rolle gibt statt nur ein Etikett – und weil es sich in den folgenden Wochen immer wieder lesen lässt.',
		},
		{
			question: 'Wann sollte man ein Geschenk für das große Geschwisterkind übergeben?',
			answer:
				'An dem Tag, an dem das Baby nach Hause kommt, oder beim ersten Krankenhausbesuch – dem Moment, in dem dem älteren Kind die Veränderung am bewusstesten ist. Da personalisierte Artikel auf Bestellung gefertigt werden, sollte man angesichts unsicherer Entbindungstermine etwa einen Monat im Voraus bestellen.',
		},
		{
			question: 'Sollte ein Geschenk für das große Geschwisterkind als vom Baby kommend präsentiert werden?',
			answer:
				'In der Regel nicht. Ältere Kinder durchschauen das meist, und es kann herablassend wirken. Es funktioniert besser, wenn es ganz offen von dem Erwachsenen kommt, der es überreicht.',
		},
	],
	ui: {
		homeLabel: 'Startseite',
		blogLabel: 'Blog',
		faqHeading: 'Häufig gestellte Fragen',
		moreReadingHeading: 'Mehr lesen',
		ctaLabel: 'Personalisierte Geschenke ansehen',
	},
};

export default post;
